// import hooks
import { useAllRides } from "hooks/use-all-ride-data";
import { getUniqueObjects } from "helpers/data/get-unique-objects";
import { RideFilter } from "types/filters";

export const getRidesForSignatureEventLocations = (
    signatureEventLocations: { id: string | undefined }[]
): Queries.RideFieldsFragment[] => {
    const rides = useAllRides();

    return rides.filter((ride) => {
        return ride.signatureEventDetails?.some((detail) =>
            signatureEventLocations.some(
                (location) => location.id === detail?.id
            )
        );
    });
};

export const getRidesForSignatureEventUniversalTag = (
    signatureEventId: string,
    universalTagId: string
): Queries.RideFieldsFragment[] => {
    const rides = useAllRides();

    return rides.filter((ride) => {
        return ride.signatureEventDetails?.some(
            (detail) =>
                detail?.signatureEvent?.id === signatureEventId &&
                ride.universalTags?.some((tag) => tag?.id === universalTagId)
        );
    });
};

export const getRidesForBuilding = (
    buildingId: string
): Queries.RideFieldsFragment[] => {
    const rides = useAllRides();

    return rides.filter((ride) => {
        return ride.signatureEventDetails?.some((detail) => {
            return detail?.locations?.some((location) => {
                const isTargetBuilding =
                    location?.__typename === "DatoCmsBuilding" &&
                    location.id === buildingId;
                const isLocationInBuilding =
                    location?.__typename === "DatoCmsLocation" &&
                    location.building?.id === buildingId;

                return isTargetBuilding || isLocationInBuilding;
            });
        });
    });
};

export const getRidesForFilters = (
    filters: RideFilter[],
    signatureEventId?: string
): Queries.RideFieldsFragment[] => {
    // if there are no filters and no signature event, return all rides
    if (filters.length === 0 && !signatureEventId) {
        return useAllRides();
    }

    // if there are no filters, return all rides for signature event
    if (filters.length === 0) {
        return useAllRides().filter((ride) =>
            ride.signatureEventDetails?.some(
                (detail) => detail?.signatureEvent?.id === signatureEventId
            )
        );
    }

    // else filter rides based on filters
    const ridesForUniversalTags = getRidesForUniveralTags(
        filters,
        signatureEventId
    );

    const ridesForRideType = getRidesForRideType(filters, signatureEventId);

    const ridesForCosts = getRidesForCosts(filters, signatureEventId);

    const ridesForAgeGroups = getRidesForAgeGroups(filters, signatureEventId);

    const commonRides = getCommonRides(
        ridesForUniversalTags,
        ridesForRideType,
        ridesForCosts,
        ridesForAgeGroups
    );

    return commonRides;
};

export const getRidesForUniveralTags = (
    filters: RideFilter[],
    signatureEventId?: string
): Queries.RideFieldsFragment[] => {
    const rides = useAllRides();

    // get filters where __typename = DatoCmsUniversalTag
    const universalTagFilters = filters.filter(
        (filter) => filter.__typename === "DatoCmsUniversalTag"
    );

    if (universalTagFilters.length === 0) {
        return [];
    }

    return rides.filter((ride) => {
        // Check for signatureEventId and matching details
        if (
            signatureEventId &&
            !ride.signatureEventDetails?.some(
                (detail) => detail?.signatureEvent?.id === signatureEventId
            )
        ) {
            return false; // No matches found, exclude this ride
        }

        // Check for matching universal tag filters
        return ride.universalTags?.some((tag) =>
            universalTagFilters.some((filter) => filter.id === tag?.id)
        );
    });
};

export const getRidesForRideType = (
    filters: RideFilter[],
    signatureEventId?: string
): Queries.RideFieldsFragment[] => {
    const rides = useAllRides();

    // get filters where __typename = DatoCmsRideType
    const rideTypeFilters = filters.filter(
        (filter) => filter.__typename === "DatoCmsRideType"
    );

    if (rideTypeFilters.length === 0) {
        return [];
    }

    return rides.filter((ride) => {
        // Check for signatureEventId and matching details
        if (
            signatureEventId &&
            !ride.signatureEventDetails?.some(
                (detail) => detail?.signatureEvent?.id === signatureEventId
            )
        ) {
            return false; // No matches found, exclude this ride
        }

        // Check for matching ride type filters
        return rideTypeFilters.some(
            (filter) => filter.id === ride.rideType?.id
        );
    });
};

export const getRidesForCosts = (
    filters: RideFilter[],
    signatureEventId?: string
): Queries.RideFieldsFragment[] => {
    const rides = useAllRides();

    // get filters where __typename = DatoCmsCost
    const costFilters = filters.filter(
        (filter) => filter.__typename === "DatoCmsCost"
    );

    if (costFilters.length === 0) {
        return [];
    }

    return rides.filter((ride) => {
        // Check for signatureEventId and matching details
        if (
            signatureEventId &&
            !ride.signatureEventDetails?.some(
                (detail) => detail?.signatureEvent?.id === signatureEventId
            )
        ) {
            return false; // No matches found, exclude this ride
        }

        // Check for matching cost filters
        return costFilters.some((filter) => filter.id === ride.cost?.id);
    });
};

export const getRidesForAgeGroups = (
    filters: RideFilter[],
    signatureEventId?: string
): Queries.RideFieldsFragment[] => {
    const rides = useAllRides();

    // get filters where __typename = DatoCmsAgeGroup
    const ageGroupFilters = filters.filter(
        (filter) => filter.__typename === "DatoCmsAgeGroup"
    );

    if (ageGroupFilters.length === 0) {
        return [];
    }

    return rides.filter((ride) => {
        // Check for signatureEventId and matching details
        if (
            signatureEventId &&
            !ride.signatureEventDetails?.some(
                (detail) => detail?.signatureEvent?.id === signatureEventId
            )
        ) {
            return false; // No matches found, exclude this ride
        }

        // Check for matching age group filters
        return ride.ageGroups?.some((tag) =>
            ageGroupFilters.some((filter) => filter.id === tag?.id)
        );
    });
};

function getCommonRides(
    ...rideArrays: Queries.RideFieldsFragment[][]
): Queries.RideFieldsFragment[] {
    // Filter out empty arrays
    const nonEmptyArrays = rideArrays.filter(
        (array) => array.length > 0
    );
    
    if (nonEmptyArrays.length === 0) return [];

    // Extract ids from each array
    const idSets = nonEmptyArrays.map(
        (array) => new Set(array.map((ride) => ride.id))
    );

    // Find common ids
    const commonIds = [...idSets[0]].filter((id) =>
        idSets.every((set) => set.has(id))
    );

    // Filter rides to only include those with common ids
    const commonRides = nonEmptyArrays[0].filter((ride) =>
        commonIds.includes(ride.id)
    );

    // Remove any duplicate performers which existed in multiple input arrays
    return getUniqueObjects(commonRides);
}